import type { CognitoTokens } from './cognito_service';

export const TOKENS_STORAGE_KEY = 'voll.auth.tokens';
export const GENERATION_KEY = 'voll.auth.generation';
export const TOMBSTONE_KEY = 'voll.auth.signedout';
export const OIDC_USER_PREFIX = 'voll.auth.user.';
export const REFRESH_LEEWAY_MS = 60_000;

const session = (): Storage => globalThis.sessionStorage;

// Syntactically-valid JSON is not enough: a partial blob like `{}` would slip
// through `as CognitoTokens` and fool `isUsableTokenSet` (`undefined !== null`),
// making a broken entry masquerade as a usable session and suppressing the
// coordinator's peer request. Validate every required field.
function isCognitoTokens(value: unknown): value is CognitoTokens {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.accessToken === 'string' &&
    typeof t.idToken === 'string' &&
    (typeof t.refreshToken === 'string' || t.refreshToken === null) &&
    typeof t.expiresAt === 'number' &&
    Number.isFinite(t.expiresAt) &&
    typeof t.tokenType === 'string'
  );
}

export function readTokens(storage: Storage = session()): CognitoTokens | null {
  const raw = storage.getItem(TOKENS_STORAGE_KEY);
  // Only an absent key is a clean miss; an empty string is invalid data and must
  // fall through to the removeItem cleanup below (via the parse failure).
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isCognitoTokens(parsed)) return parsed;
  } catch {
    // fall through to cleanup
  }
  storage.removeItem(TOKENS_STORAGE_KEY);
  return null;
}

export function writeTokens(tokens: CognitoTokens | null, storage: Storage = session()): void {
  if (tokens) storage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  else storage.removeItem(TOKENS_STORAGE_KEY);
}

export function isUsableTokenSet(tokens: CognitoTokens | null): boolean {
  if (!tokens) return false;
  if (tokens.expiresAt > Date.now() + REFRESH_LEEWAY_MS) return true;
  return tokens.refreshToken !== null;
}

function readNumber(key: string, storage: Storage): number {
  const raw = storage.getItem(key);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export const readGeneration = (storage: Storage = session()): number =>
  readNumber(GENERATION_KEY, storage);
export const writeGeneration = (gen: number, storage: Storage = session()): void => {
  storage.setItem(GENERATION_KEY, String(gen));
};
export const clearGeneration = (storage: Storage = session()): void => {
  storage.removeItem(GENERATION_KEY);
};

export const readTombstone = (storage: Storage = session()): number =>
  readNumber(TOMBSTONE_KEY, storage);
export const writeTombstone = (ts: number, storage: Storage = session()): void => {
  storage.setItem(TOMBSTONE_KEY, String(ts));
};
export const clearTombstone = (storage: Storage = session()): void => {
  storage.removeItem(TOMBSTONE_KEY);
};
